
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { AuthModal } from "@/components/AuthModal";
import { Loader2 } from "lucide-react";
import { V2LoadingScreen } from "@/components/v2/V2LoadingScreen";
import { isDespiaIOS } from "@/hooks/use-despia";

/** Lightweight full-screen spinner – NOT the video loading animation */
function AuthLoadingScreen() {
  return (
    <div className="h-screen w-screen flex items-center justify-center bg-background fixed inset-0 z-50">
      <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
    </div>
  );
}

interface RequireAuthProps {
  children: React.ReactNode;
  requireProfileSelection?: boolean;
}

export function RequireAuth({ children, requireProfileSelection = true }: RequireAuthProps) {
  const { session, isLoading, profileSelectionComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    isAuthModalOpen, 
    openAuthModal, 
    closeAuthModal,
    openProfileSelection,
    closeProfileSelection
  } = useAuthModal();

  const isV2Route = location.pathname.startsWith('/v2');
  const isNativeIOS = isDespiaIOS();

  // For Despia iOS / V2: skip the isInitialized double-gate entirely.
  // profileSelectionComplete is already true from AuthProvider, so we
  // only need to wait for auth loading to finish.
  const canRenderImmediately = isNativeIOS || isV2Route;

  // Legacy web flow still uses the initialized gate
  const [isInitialized, setIsInitialized] = useState(canRenderImmediately);

  const checkProfileSelection = useCallback(() => {
    if (isLoading || !session) return false;

    // Check if we're processing a payment - if so, don't redirect to profiles
    const isProcessingPayment = sessionStorage.getItem('processing-payment') === 'true';
    if (isProcessingPayment) {
      return true;
    }

    // Check if we have payment parameters in URL
    const searchParams = new URLSearchParams(location.search);
    const hasPaymentParams = searchParams.get('success') || searchParams.get('canceled') || searchParams.get('session_id');
    if (hasPaymentParams) {
      return true;
    }

    if (requireProfileSelection && !profileSelectionComplete) {
      return false;
    }
    
    return true;
  }, [session, isLoading, requireProfileSelection, profileSelectionComplete, location.search]);

  useEffect(() => {
    if (isLoading) return;
    // V2/iOS: no extra initialization needed
    if (canRenderImmediately) return;

    if (!session) {
      // V2 routes: redirect to v2 iosflow onboarding instead of home
      if (isV2Route) {
        navigate("/v2/iosflow", { replace: true });
        return;
      }
      navigate("/", { replace: true });
      openAuthModal("Please sign in to continue reading");
      return;
    }

    const canProceed = checkProfileSelection();
    if (canProceed) {
      setIsInitialized(true);
    }
  }, [session, isLoading, checkProfileSelection, navigate, openAuthModal, canRenderImmediately, isV2Route]);

  const handleAuthSuccess = () => {
    closeAuthModal();
  };

  // ─── V2 / iOS fast path: only wait for auth, skip all other gates ───
  if (canRenderImmediately) {
    if (isLoading) {
      return <V2LoadingScreen />;
    }
    if (!session) {
      // Unauthenticated on V2 — redirect handled by useEffect above or AuthAwareIndex
      return <V2LoadingScreen />;
    }
    // Authenticated — render children immediately
    return <>{children}</>;
  }

  // ─── Legacy web flow ───
  if (isLoading || !isInitialized) {
    return <AuthLoadingScreen />;
  }

  if (!session) {
    return (
      <>
        <AuthLoadingScreen />
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={closeAuthModal}
          onAuthSuccess={handleAuthSuccess}
          onTransitionToProfileSelection={() => {
            closeAuthModal();
            openProfileSelection();
          }}
          context="Please sign in to continue reading"
        />
      </>
    );
  }

  if (requireProfileSelection && !profileSelectionComplete) {
    return <AuthLoadingScreen />;
  }

  return <>{children}</>;
}
